import { useQueryClient, QueryKey } from "@tanstack/vue-query";
import { ref } from "vue";

import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";

export function useFetch<TArgs extends unknown[], T, TQueryKey extends QueryKey = QueryKey>(
  queryKey: (...args: TArgs) => TQueryKey,
  fn: (...args: TArgs) => Promise<T>,
) {
  const queryClient = useQueryClient();
  const isFetching = ref<boolean>(false);

  async function fetch(...args: TArgs): Promise<T | undefined> {
    const key = queryKey(...args);
    const maybeData = queryClient.getQueryData<T>(key);

    if (maybeData) {
      return maybeData;
    }

    function fetcher() {
      return fn(...args);
    }

    try {
      isFetching.value = true;

      return await queryClient.fetchQuery({
        queryKey: key,
        queryFn: fetcher,
      });
    } catch (error) {
      toastKiliError(error);
      throw error;
    } finally {
      isFetching.value = false;
    }
  }

  return {
    isFetching,
    fetch,
  };
}
